import { GridRowModel } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";

import { GenericObject } from "../../services/Price";

// The assigned model's family, as the `{ id }` seed of the family select
// (`row.model.family` is a bare id; the select resolves the name).
export const modelFamilyFromRow = (row: GridRowModel) =>
  row.model?.family != null ? { id: row.model.family } : null;

// ISO weekday (1 Monday … 7 Sunday, the `/api/models/` `week_day` convention)
// of the calendar date. `date_at` is serialized as a datetime: read the
// weekday in its own zone rather than the browser's, or a midnight UTC date
// shifts to the day before for users west of Greenwich.
export const dateWeekDay = (row: GridRowModel): number =>
  DateTime.fromISO(row.date_at, { setZone: true }).weekday;

// Whether the model being assigned belongs to another property than the
// row's. Read on the resolved model itself (both lookups write its
// `property`), not on the fields: a reused calendar row may itself borrow a
// model from elsewhere. Including such a date would mix two properties'
// history in the model curve.
export const isModelFromOtherProperty = (
  row: GridRowModel | undefined,
  values: GenericObject
): boolean => {
  const ownId = row?.property?.id;
  const modelPropertyId = values.model?.property;
  return ownId != null && modelPropertyId != null && modelPropertyId !== ownId;
};

// A date still to come has no realised pickup to feed the model curve, so
// "included" has nothing to say about it. Compared on calendar days.
export const isFutureDate = (row: GridRowModel | undefined): boolean =>
  !!row?.date_at &&
  DateTime.fromISO(row.date_at, { setZone: true }).startOf("day") >
    DateTime.now().startOf("day");

// Opening values of the model's hotel and day: the current model's own when
// one is assigned, else the row's hotel and the day of its date.
// `SelectDayOfWeek` holds its value as a "1"…"7" string.
export const modelWeekDayFromRow = (row: GridRowModel): string =>
  String(row.model?.week_day ?? dateWeekDay(row));

// `row.model.property` is a bare id (ModelSimpleSerializer): the select
// resolves its label from the loaded hotels.
export const modelPropertyFromRow = (row: GridRowModel) =>
  row.model?.property != null && row.model.property !== row.property?.id
    ? { id: row.model.property }
    : row.property;
